'use client';

import React, { FC, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';

import { GenInternalLink, GenNavTile } from '@/components';
import { TypeAccountNavTileFields } from '@/core/generated-types';
import { TypeUserRecord } from '@/lib/api/accountManager/types';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import { getCountryCodeFromLocale } from '../../EditCommunicationPreferences/PhoneNumberInput';
import AddPhoneNumberForm from '@/components/common/EditCommunicationPreferences/AddPhoneNumberForm';
import { isEuStore } from '@/utils/environment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getLabelForLanguage,
  stringToLanguage,
} from '@/components/common/EditCommunicationPreferences/utils';
import { FeatureFlag } from '@/core/featureFlags';

export type CommunicationPreferencesTileProps = {
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
};

const _getParsedNumber = (phoneNumber: string, locale: string): string => {
  const parsedNumber = parsePhoneNumberFromString(phoneNumber);

  if (parsedNumber) {
    return locale === 'US'
      ? parsedNumber?.formatNational()
      : parsedNumber?.formatInternational();
  } else {
    return '';
  }
};

const DataDisplay = ({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) => {
  return (
    <div
      className="flex flex-col lg:flex-row space-x-1 mt-2"
      data-testid={testId}
    >
      <strong>{label}</strong>
      <span className="break-all">{value}</span>
    </div>
  );
};

const CommunicationPreferencesTile: FC<CommunicationPreferencesTileProps> = ({
  t,
  user,
  tile,
  locale,
}) => {
  const { title, subheader, description, subfooter } = tile;
  const { [FeatureFlag.RETENTION_ENABLE_SMS_MESSAGING]: enableSmsMessaging } =
    useFlags();

  const [phoneNumber, setPhoneNumber] = useState<string>(
    user.phone_number || '',
  );
  const editCommunicationPreferencesUrl = `/account/settings/edit-communication-preferences`;
  const countryCode = getCountryCodeFromLocale(locale);
  const isUserPhoneNumberPresent = Boolean(user.phone_number?.trim());
  const parsedNumber = _getParsedNumber(phoneNumber, countryCode);

  return (
    <GenNavTile
      title={title}
      titleDecorator={
        <GenInternalLink
          href={editCommunicationPreferencesUrl}
          cta={t.editCta}
        />
      }
    >
      <p className="mb-6">{description}</p>

      {enableSmsMessaging && (
        <div data-testid="smsNotificationsSection">
          {isUserPhoneNumberPresent ? (
            <DataDisplay
              label={t.phoneNumberLabel}
              value={parsedNumber}
              testId="phoneNumberDisplay"
            />
          ) : (
            <AddPhoneNumberForm
              user={user}
              header={t.smsNotificationsLabel}
              subheader={subheader}
              subfooter={subfooter}
              successMessage={t.smsSuccessMessage}
              submitCta={t.addMyNumberCta}
              invalidNumberMessage={t.invalidPhoneNumberMessage}
              phonePlaceholderLabel={t.phonePlaceholderLabel}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              classes="mb-4"
            />
          )}
        </div>
      )}

      <DataDisplay
        label={t.emailLabel}
        value={user.email}
        testId="emailDisplay"
      />
      {isEuStore() && user.language && (
        <DataDisplay
          label={t.languageLabel}
          value={getLabelForLanguage(stringToLanguage(user.language), t)}
          testId="languageDisplay"
        />
      )}
    </GenNavTile>
  );
};

export default React.memo(CommunicationPreferencesTile);
